//#region Imports
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { styled, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import SearchResults, { Hit } from "_shared/_api/search/search-results";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { Dictionary } from "lodash";
import { marketplaceApi } from "marketplace/_api/marketplace-api";
import MarketplaceSearchRequestSchema, { MarketplaceSearchRequestJSON } from "marketplace/_schema/marketplace-search-request-schema";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import {
  AssistedFilterKey,
  AssistedSearchFallbackReason,
  AssistedSearchFiltersJSON,
  AssistedSearchMode,
  getAssistedFilterLabel,
  isAssistedFilterKey,
  ParsedFilterChip,
  SuppressedInferencesQueryParam,
  toAssistedExplicitFilters,
  toParsedFilterChips,
} from "marketplace/_schema/assisted-search-schema";
import ResultSkeleton from "marketplace/_views/result-skeleton";
import ResultsList from "marketplace/_views/results-list";
import Individual from "marketplace/individuals/individual";
import MarketplaceTeamSearchSchema, { MarketplaceTeamSearchJSON } from "marketplace/teams/_api/_schemas/marketplace-team-search-schema";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { ChangeEvent, useCallback, useEffect } from "react";
import { NavigateFunction, SetURLSearchParams } from "react-router";
import Session from "users/__session/session";
//#endregion

const ComponentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    paddingRight: 0,
  },
  display: "flex",
}));
const ResultCount = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginLeft: theme.spacing(2),
  },
}));
const LowerPagination = styled(Grid)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
}));
//#region Styles
const UpperPagination = styled(Grid)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));
const AssistedNotice = styled("div")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: "rgba(225, 153, 65, 0.12)",
  borderRadius: theme.spacing(0.5),
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
}));
const AssistedModeIndicator = styled("div")(({ theme }) => ({
  alignItems: "center",
  color: theme.palette.text.secondary,
  display: "flex",
  gap: theme.spacing(0.5),
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
}));
//#endregion

type ResultsProps = {
  session: Readonly<Session>;
  searchTerm: string;
  category: string;
  aiMode?: boolean;
  onParsedFiltersApplied?: (chips: ParsedFilterChip[]) => void;
  filters: Dictionary<string>;
  sortBy: string;
  embedded?: boolean;
  selectingTeamLeader?: boolean;
  selectingTeamMember?: boolean;
  selectingTeam?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  compactLayout?: boolean;
  navigate?: NavigateFunction;
  searchParams?: URLSearchParams;
  setSearchParams?: SetURLSearchParams;
  onResults: (results: number | undefined) => void;
  onTeamMemberSelected?: (teamMember: Individual) => void;
  onTeamSelected?: (team: Team) => void;
//#endregion
};

type AssistedOutcome =
  | "none"
  | "assisted"
  | "literal"
  | "featureDisabled"
  | "parseUnavailable"
  | "parseTimedOut"
  | "retrievalUnavailable"
  | "deadlineExceeded"
  | "noAuthorizedCandidates"
  | "requestError";

type SearchRequestContext = {
  generation: number;
  signature: string;
};

type AbortableRequest = {
  abort?: () => void;
};

function toAssistedOutcome(
  mode: AssistedSearchMode,
  fallbackReason: AssistedSearchFallbackReason | null | undefined,
): AssistedOutcome {
  if (mode === "Assisted") return "assisted";

  switch (fallbackReason) {
    case "FeatureDisabled":
      return "featureDisabled";
    case "IntentParseUnavailable":
      return "parseUnavailable";
    case "IntentParseTimedOut":
      return "parseTimedOut";
    case "EmbeddingUnavailable":
    case "SemanticRetrievalUnavailable":
    case "RerankUnavailable":
      return "retrievalUnavailable";
    case "DeadlineExceeded":
      return "deadlineExceeded";
    case "NoAuthorizedCandidates":
      return "noAuthorizedCandidates";
    case "None":
    case null:
    case undefined:
      return "literal";
  }
}

function getAssistedOutcomeMessage(outcome: AssistedOutcome): string | undefined {
  switch (outcome) {
    case "featureDisabled":
      return "AI search is turned off right now, so these are keyword matches.";
    case "parseUnavailable":
      return "AI search could not read your request right now, so these are keyword matches.";
    case "parseTimedOut":
      return "AI search took too long to read your request, so these are keyword matches.";
    case "retrievalUnavailable":
      return "AI search could not complete right now, so these are keyword matches.";
    case "deadlineExceeded":
      return "AI search took too long to complete, so these are keyword matches.";
    case "noAuthorizedCandidates":
      return "AI search could not return a usable result set, so these are keyword matches.";
    case "requestError":
      return "AI search is unavailable right now, so these are keyword matches.";
    case "literal":
      return "AI search used the exact words in your request for these matches.";
    case "none":
    case "assisted":
      return undefined;
  }
}

function getPageIndex(searchParams: URLSearchParams | undefined): number {
  const parsed = Number.parseInt(searchParams?.get("pageIndex") ?? "0", 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function getSuppressedInferences(searchParams: URLSearchParams | undefined): AssistedFilterKey[] {
  const values = searchParams
    ?.getAll(SuppressedInferencesQueryParam)
    .flatMap((value) => value.split(","))
    .map((value) => value.trim())
    .filter(isAssistedFilterKey) ?? [];

  return [...new Set(values)];
}

function hasExplicitValue(filterName: string, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return filterName === "entityTypes" || value.length > 0;
  return typeof value !== "string" || value.length > 0;
}

function isExpectedAbortError(error: unknown): boolean {
  const e = error as
    | { name?: string; message?: string; data?: unknown }
    | undefined;

  const message =
    e?.message ??
    (typeof e?.data === "string"
      ? e.data
      : (e?.data as { message?: string } | undefined)?.message);

  return (
    e?.name === "AbortError" ||
    e?.name === "ConditionError" ||
    (typeof message === "string" &&
      message.includes("Aborted due to condition callback returning false"))
  );
}

/**
 * Everything `fetchAssistedResults` does, lifted out of the component.
 *
 * The routine needs `try/catch/finally` and a `throw` inside the `try`: the loading flag has to be
 * cleared on every exit path, and the error paths converge on one handler. The React Compiler can
 * lower neither, and a single unlowerable statement anywhere inside a component silently drops
 * memoization for that whole component. Module-level functions are not compiled, so keeping this
 * here preserves the control flow exactly rather than contorting it to suit the compiler.
 *
 * See app/CLAUDE.md -> React Compiler.
 */
type AssistedSearchDeps = {
  dispatch: ReturnType<typeof useAttorneyHubDispatch>;
  failedAssistedAttemptRef: React.MutableRefObject<string>;
  isLatestRequest(context: SearchRequestContext): boolean;
  clearAssistedQueryState(context: SearchRequestContext | undefined, outcome?: AssistedOutcome): void;
  clearSuppressedInferences(context?: SearchRequestContext): void;
  resetOutOfRangePage(authoritativeTotal: number, responseFrom: number, context: SearchRequestContext): boolean;
  trackRunningRequest(runningRequest: unknown, startedRequest: unknown, context: SearchRequestContext): void;
  setAssistedLoading(value: boolean): void;
  setResults(hits: Hit<MarketplaceSearchInfoJSON>[]): void;
  setNumResults(total: number): void;
  setRowsPerPage(size: number): void;
  setPage(page: number): void;
  setDeclinedConstraints(constraints: string[]): void;
  setSupersededInferences(keys: AssistedFilterKey[]): void;
  setAssistedOutcome(outcome: AssistedOutcome): void;
  onResults(total: number | undefined): void;
  onParsedFiltersApplied?: (chips: ReturnType<typeof toParsedFilterChips>) => void;
};

async function runAssistedSearch(
  term: string,
  explicitFilters: AssistedSearchFiltersJSON,
  suppressedInferences: AssistedFilterKey[],
  from: number,
  size: number,
  assistedSortBy: string | undefined,
  context: SearchRequestContext,
  attemptKey: string,
  deps: AssistedSearchDeps,
): Promise<boolean> {
  const {
    dispatch,
    failedAssistedAttemptRef,
    isLatestRequest,
    clearAssistedQueryState,
    clearSuppressedInferences,
    resetOutOfRangePage,
    trackRunningRequest,
    setAssistedLoading,
    setResults,
    setNumResults,
    setRowsPerPage,
    setPage,
    setDeclinedConstraints,
    setSupersededInferences,
    setAssistedOutcome,
    onResults,
    onParsedFiltersApplied,
  } = deps;

  if (!isLatestRequest(context)) return true;

  if (!term.trim()) {
    clearAssistedQueryState(context);
    clearSuppressedInferences(context);
    setAssistedLoading(false);
    return false;
  }

  failedAssistedAttemptRef.current = "";
  setAssistedLoading(true);
  clearAssistedQueryState(context);

  try {
    const assistedArgs = {
      query: term,
      explicitFilters,
      suppressedInferences,
      from,
      size,
      sortBy: assistedSortBy,
    };
    const assistedRequest = dispatch(
      marketplaceApi.endpoints.searchAssisted.initiate(
        assistedArgs,
        { subscribe: false, forceRefetch: true },
      ),
    );
    const runningAssistedRequest = dispatch(
      marketplaceApi.util.getRunningQueryThunk("searchAssisted", assistedArgs),
    );
    trackRunningRequest(runningAssistedRequest, assistedRequest, context);
    const assisted = await assistedRequest;

    if (!isLatestRequest(context)) return true;

    if ("error" in assisted && assisted.error) {
      // A condition abort means another in-flight request owns this search and will report its
      // own outcome. Saying anything here would describe a request that was never really made.
      if (isExpectedAbortError(assisted.error)) return true;
      throw assisted.error;
    }

    if (!("data" in assisted) || !assisted.data) {
      failedAssistedAttemptRef.current = attemptKey;
      clearAssistedQueryState(context, "requestError");
      clearSuppressedInferences(context);
      return false;
    }

    const assistedMode = assisted.data.modeUsed === "Assisted";
    const hasEffectiveFilters = Object.entries(assisted.data.effectiveFilters ?? {})
      .some(([key, value]) => hasExplicitValue(key, value));
    const hasSearchTerms = Boolean(assisted.data.parsedIntent?.searchTerms?.trim());

    // A successful parser response with no usable filter or literal term must not display the
    // endpoint's unfiltered result set. Run the original query through keyword search instead.
    if (assistedMode && !hasEffectiveFilters && !hasSearchTerms) {
      clearAssistedQueryState(context, "literal");
      return false;
    }

    const hits = (assisted.data.hits ?? []).map((hit) => ({
      data: hit.data as MarketplaceSearchInfoJSON,
      score: hit.score ?? 0,
    }));

    const responseSize = assisted.data.size || size;
    const responseFrom = assisted.data.from ?? from;
    const total = assisted.data.total ?? hits.length;
    if (resetOutOfRangePage(total, responseFrom, context)) return true;
    const superseded = assistedMode
      ? (assisted.data.supersededInferences ?? []).filter(isAssistedFilterKey)
      : [];
    const excludedInferenceKeys = new Set<string>([
      ...suppressedInferences,
      ...superseded,
      ...Object.entries(explicitFilters)
        .filter(([key, value]) => hasExplicitValue(key, value))
        .map(([key]) => key),
    ]);
    const inferredChips = assistedMode
      ? toParsedFilterChips(assisted.data.parsedIntent?.inferredFilters)
          .filter((chip) => !excludedInferenceKeys.has(chip.filterName))
      : [];

    if (!isLatestRequest(context)) return true;

    setResults(hits);
    setNumResults(total);
    setRowsPerPage(responseSize);
    setPage(Math.floor(responseFrom / responseSize) + 1);
    onResults(total);
    onParsedFiltersApplied?.(inferredChips);
    setDeclinedConstraints(assistedMode ? assisted.data.parsedIntent?.declinedConstraints ?? [] : []);
    setSupersededInferences(superseded);
    setAssistedOutcome(toAssistedOutcome(assisted.data.modeUsed, assisted.data.fallbackReason));
    return true;
  } catch (error) {
    if (!isLatestRequest(context)) return true;
    console.error(error);
    failedAssistedAttemptRef.current = attemptKey;
    clearAssistedQueryState(context, "requestError");
    clearSuppressedInferences(context);
    return false;
  } finally {
    if (isLatestRequest(context)) setAssistedLoading(false);
  }
}

  //#region Component
export default function Results(props: Readonly<ResultsProps>) {
  const {
    session,
    searchTerm,
    category,
    aiMode,
    onParsedFiltersApplied,
    filters,
    sortBy,
    embedded,
    selectingTeamLeader,
    selectingTeamMember,
    selectingTeam,
    selectedTeamMemberUserIds,
    compactLayout,
    searchParams,
    setSearchParams,
    navigate,
    onResults,
    onTeamMemberSelected,
    onTeamSelected,
  //#endregion
  } = props;

  const dispatch = useAttorneyHubDispatch();
  const isMarketplaceSearchMode = !selectingTeam && !selectingTeamLeader && !selectingTeamMember;
  const canUseAssistedSearch = isMarketplaceSearchMode && (!category || category === "all");

  const [searchAllArgs, setSearchAllArgs] = React.useState<{ search: MarketplaceSearchRequestJSON } | undefined>();
  const [searchIndividualsArgs, setSearchIndividualsArgs] = React.useState<
    { search: MarketplaceSearchRequestJSON; limitToVendorReps?: boolean } | undefined
  >();
  const [searchTeamsArgs, setSearchTeamsArgs] = React.useState<{ search: MarketplaceTeamSearchJSON } | undefined>();
  const [individualByIdArgs, setIndividualByIdArgs] = React.useState<{ individualId: string } | undefined>();

  const searchAllQueryState = marketplaceApi.endpoints.searchAll.useQueryState(searchAllArgs ?? skipToken);
  const searchIndividualsQueryState = marketplaceApi.endpoints.searchIndividuals.useQueryState(
    searchIndividualsArgs ?? skipToken,
  );
  const searchTeamsQueryState = marketplaceApi.endpoints.searchTeams.useQueryState(searchTeamsArgs ?? skipToken);
  const individualByIdQueryState = marketplaceApi.endpoints.getMarketplaceIndividualById.useQueryState(
    individualByIdArgs ?? skipToken,
  );

  const marketplaceSearchLoading = searchAllQueryState.isLoading || searchAllQueryState.isFetching;
  const selectionModeLoading =
    searchIndividualsQueryState.isLoading ||
    searchIndividualsQueryState.isFetching ||
    searchTeamsQueryState.isLoading ||
    searchTeamsQueryState.isFetching ||
    individualByIdQueryState.isLoading ||
    individualByIdQueryState.isFetching;

  const [results, setResults] = React.useState<Hit<MarketplaceSearchInfoJSON>[]>([]);
  const [page, setPage] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
  const [numResults, setNumResults] = React.useState<number>(0);
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"), {
    matchMedia: globalThis.matchMedia,
  });

  const hasInitializedQueryEffectRef = React.useRef(false);
  const lastQuerySignatureRef = React.useRef<string>("");
  const requestGenerationRef = React.useRef(0);
  const activeRequestSignatureRef = React.useRef("");
  const activeRequestRef = React.useRef<AbortableRequest | undefined>(undefined);
  const mountedRef = React.useRef(false);
  const failedAssistedAttemptRef = React.useRef("");
  const lastQueryTermRef = React.useRef(searchParams?.get("query") ?? searchTerm ?? "");

  const [assistedLoading, setAssistedLoading] = React.useState(false);
  const [queryEffectPending, setQueryEffectPending] = React.useState(true);
  const [assistedOutcome, setAssistedOutcome] = React.useState<AssistedOutcome>("none");
  const [retryNonce, setRetryNonce] = React.useState(0);
  // Words the parser recognized but had no field for. Showing them is not optional: they were part
  // of what the user asked for and the search ignored them, so results that look wrong are only
  // explicable if the page says which part of the sentence it could not use.
  const [declinedConstraints, setDeclinedConstraints] = React.useState<string[]>([]);
  const [supersededInferences, setSupersededInferences] = React.useState<AssistedFilterKey[]>([]);
  // Query subscriptions are attached from the effect below. Treat the first render as pending so
  // the page does not announce "0 Results" before that effect has had a chance to start the request.
  const loading = queryEffectPending || (isMarketplaceSearchMode
    ? marketplaceSearchLoading || assistedLoading
    : selectionModeLoading);

  const isLatestRequest = useCallback(
    (context: SearchRequestContext) =>
      mountedRef.current &&
      requestGenerationRef.current === context.generation &&
      activeRequestSignatureRef.current === context.signature,
    [],
  );

  const beginSearchRequest = useCallback((signature: string): SearchRequestContext => {
    // The signature below contains only values that change the effective request. Cosmetic URL
    // normalization therefore reuses the in-flight request, while a genuinely different search
    // still cancels backend/database/provider work that can no longer affect this page.
    activeRequestRef.current?.abort?.();
    activeRequestRef.current = undefined;
    const context = {
      generation: requestGenerationRef.current + 1,
      signature,
    };
    requestGenerationRef.current = context.generation;
    activeRequestSignatureRef.current = signature;
    return context;
  }, []);

  const invalidateActiveRequest = useCallback(() => {
    requestGenerationRef.current += 1;
    activeRequestSignatureRef.current = "";
    activeRequestRef.current?.abort?.();
    activeRequestRef.current = undefined;
  }, []);

  const trackActiveRequest = useCallback(
    (request: unknown, context: SearchRequestContext) => {
      if (isLatestRequest(context)) activeRequestRef.current = request as AbortableRequest;
    },
    [isLatestRequest],
  );

  const trackRunningRequest = useCallback(
    (runningRequest: unknown, startedRequest: unknown, context: SearchRequestContext) => {
      const runningAbortable = runningRequest as AbortableRequest | undefined;
      trackActiveRequest(runningAbortable?.abort ? runningAbortable : startedRequest, context);
    },
    [trackActiveRequest],
  );

  const clearAssistedQueryState = useCallback(
    (context: SearchRequestContext | undefined, outcome: AssistedOutcome = "none") => {
      if (context && !isLatestRequest(context)) return;
      onParsedFiltersApplied?.([]);
      setDeclinedConstraints([]);
      setSupersededInferences([]);
      setAssistedOutcome(outcome);
    },
    [isLatestRequest, onParsedFiltersApplied],
  );

  const clearSuppressedInferences = useCallback(
    (context?: SearchRequestContext) => {
      if (context && !isLatestRequest(context)) return;
      if (!searchParams?.has(SuppressedInferencesQueryParam) || !setSearchParams) return;
      const params = new URLSearchParams(searchParams);
      params.delete(SuppressedInferencesQueryParam);
      setSearchParams(params, { replace: true });
    },
    [isLatestRequest, searchParams, setSearchParams],
  );

  const resetOutOfRangePage = useCallback(
    (authoritativeTotal: number, responseFrom: number, context: SearchRequestContext): boolean => {
      if (!isLatestRequest(context)) return true;
      if (responseFrom <= 0 || responseFrom < authoritativeTotal) return false;
      if (!searchParams || !setSearchParams || !isMarketplaceSearchMode) return false;

      const params = new URLSearchParams(searchParams);
      if (params.get("pageIndex") === "0") return false;

      setResults([]);
      setPage(1);
      onResults(undefined);
      params.set("pageIndex", "0");
      setSearchParams(params, { replace: true });
      return true;
    },
    [isLatestRequest, isMarketplaceSearchMode, onResults, searchParams, setSearchParams],
  );

  useEffect(
    () => {
      mountedRef.current = true;

      return () => {
        mountedRef.current = false;
        requestGenerationRef.current += 1;
        activeRequestSignatureRef.current = "";
        activeRequestRef.current = undefined;
        hasInitializedQueryEffectRef.current = false;
        lastQuerySignatureRef.current = "";

        // Do not abort the RTK request here. Development StrictMode immediately replays mount
        // effects, and a replacement Results instance can mount with the same cache key. Leaving
        // the in-flight query in RTK lets the replay/replacement join it; the generation and mounted
        // guards above prevent this retired instance from applying the eventual response. Requests
        // superseded while this instance stays mounted are still aborted by beginSearchRequest.
      };
    },
    [],
  );

  /**
   * Runs the natural-language search.
   *
   * Returns false when it could not run at all, so the caller can fall back to the ordinary search
   * rather than leaving the user with nothing. Every such return also records why, because falling
   * back is invisible from the results alone: keyword matches for a sentence usually means no
   * matches, and "the AI found nothing" and "the AI never ran" look identical on an empty page.
   * A silent fallback here once hid a server error that failed every assisted search.
   */
  const fetchAssistedResults = useCallback((
    term: string,
    explicitFilters: AssistedSearchFiltersJSON,
    suppressedInferences: AssistedFilterKey[],
    from: number,
    size: number,
    assistedSortBy: string | undefined,
    context: SearchRequestContext,
    attemptKey: string,
  ): Promise<boolean> => runAssistedSearch(
    term,
    explicitFilters,
    suppressedInferences,
    from,
    size,
    assistedSortBy,
    context,
    attemptKey,
    {
      dispatch,
      failedAssistedAttemptRef,
      isLatestRequest,
      clearAssistedQueryState,
      clearSuppressedInferences,
      resetOutOfRangePage,
      trackRunningRequest,
      setAssistedLoading,
      setResults,
      setNumResults,
      setRowsPerPage,
      setPage,
      setDeclinedConstraints,
      setSupersededInferences,
      setAssistedOutcome,
      onResults,
      onParsedFiltersApplied,
    },
  ), [
    clearAssistedQueryState,
    clearSuppressedInferences,
    dispatch,
    isLatestRequest,
    onResults,
    onParsedFiltersApplied,
    resetOutOfRangePage,
    trackRunningRequest,
  ]);

  const fetchResultList = useCallback(async (
    searchTerm: string,
    filters: Dictionary<string> | undefined,
    options: {
      context: SearchRequestContext;
      forceRefetch?: boolean;
      from?: number;
      size?: number;
    },
  ) => {
    const { context } = options;
    if (!isLatestRequest(context)) return;

    try {
      let response:
        | SearchResults<MarketplaceSearchInfoJSON>
        | undefined;
      const from = options.from ?? 0;
      const size = options.size ?? rowsPerPage;
      const request = MarketplaceSearchRequestSchema.validateJSON({
        ...MarketplaceSearchRequestSchema.deserialize(filters),
        from,
        size,
      });

      if (selectingTeam) {
        const teamSearch = MarketplaceTeamSearchSchema.deserialize({
          from,
          size,
          query: searchTerm,
          contactPerson: filters?.contactPerson,
          description: filters?.description,
          name: filters?.name,
          team: filters?.team,
        });
        const searchTeamsArgs = { search: teamSearch };
        setSearchTeamsArgs(searchTeamsArgs);
        const searchTeamsRequest = dispatch(
          marketplaceApi.endpoints.searchTeams.initiate(
            searchTeamsArgs,
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );
        const runningTeamsRequest = dispatch(
          marketplaceApi.util.getRunningQueryThunk("searchTeams", searchTeamsArgs),
        );
        trackRunningRequest(runningTeamsRequest, searchTeamsRequest, context);
        const searchTeamsResult = await searchTeamsRequest;
        if (!isLatestRequest(context)) return;

        if ("error" in searchTeamsResult) {
          if (!isExpectedAbortError(searchTeamsResult.error)) {
            throw searchTeamsResult.error;
          }
          return;
        }

        if ("data" in searchTeamsResult) {
          response = searchTeamsResult.data;
        }
      } else if (selectingTeamLeader) {
        const searchIndividualsArgs = { search: request, limitToVendorReps: true };
        setSearchIndividualsArgs(searchIndividualsArgs);
        const searchIndividualsRequest = dispatch(
          marketplaceApi.endpoints.searchIndividuals.initiate(
            searchIndividualsArgs,
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );
        const runningIndividualsRequest = dispatch(
          marketplaceApi.util.getRunningQueryThunk("searchIndividuals", searchIndividualsArgs),
        );
        trackRunningRequest(runningIndividualsRequest, searchIndividualsRequest, context);
        const searchIndividualsResult = await searchIndividualsRequest;
        if (!isLatestRequest(context)) return;

        if ("error" in searchIndividualsResult) {
          if (!isExpectedAbortError(searchIndividualsResult.error)) {
            throw searchIndividualsResult.error;
          }
          return;
        }

        if ("data" in searchIndividualsResult) {
          response = searchIndividualsResult.data;
        }
      } else if (selectingTeamMember) {
        const searchIndividualsArgs = { search: request };
        setSearchIndividualsArgs(searchIndividualsArgs);
        const searchIndividualsRequest = dispatch(
          marketplaceApi.endpoints.searchIndividuals.initiate(
            searchIndividualsArgs,
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );
        const runningIndividualsRequest = dispatch(
          marketplaceApi.util.getRunningQueryThunk("searchIndividuals", searchIndividualsArgs),
        );
        trackRunningRequest(runningIndividualsRequest, searchIndividualsRequest, context);
        const searchIndividualsResult = await searchIndividualsRequest;
        if (!isLatestRequest(context)) return;

        if ("error" in searchIndividualsResult) {
          if (!isExpectedAbortError(searchIndividualsResult.error)) {
            throw searchIndividualsResult.error;
          }
          return;
        }

        if ("data" in searchIndividualsResult) {
          response = searchIndividualsResult.data;
        }
      } else {
        const searchAllArgs = { search: request };
        setSearchAllArgs(searchAllArgs);
        const searchRequest = dispatch(
          marketplaceApi.endpoints.searchAll.initiate(
            searchAllArgs,
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );
        const runningSearchRequest = dispatch(
          marketplaceApi.util.getRunningQueryThunk("searchAll", searchAllArgs),
        );
        trackRunningRequest(runningSearchRequest, searchRequest, context);
        const searchResult = await searchRequest;
        if (!isLatestRequest(context)) return;

        if ("error" in searchResult) {
          if (!isExpectedAbortError(searchResult.error)) {
            throw searchResult.error;
          }
        }

        if ("data" in searchResult && searchResult.data) {
          const hits = searchResult.data.hits ?? [];
          const responseSize = searchResult.data.size || size;
          const responseFrom = searchResult.data.from ?? from;
          const total = searchResult.data.total ?? hits.length;
          if (
            searchResult.data.total !== null &&
            searchResult.data.total !== undefined &&
            resetOutOfRangePage(searchResult.data.total, responseFrom, context)
          ) {
            return;
          }
          setResults(hits);
          setNumResults(total);
          setPage(Math.floor(responseFrom / responseSize) + 1);
          setRowsPerPage(responseSize);
          onResults(total);
        }

        return;
      }

      if (!response) {
        return;
      }

      if (!isLatestRequest(context)) return;

      let hits = response.hits ? [...response.hits] : [];

      // Add self to the results list if the user is selecting a vendor-team leader
      if (
        selectingTeamLeader &&
        session.user?.individualId &&
        !response.hits?.some((hit) => hit.data.id === session.user?.individualId?.value)
      ) {
        const individualByIdArgs = { individualId: session.user.individualId.value };
        setIndividualByIdArgs(individualByIdArgs);
        const selfRequest = dispatch(
          marketplaceApi.endpoints.getMarketplaceIndividualById.initiate(
            individualByIdArgs,
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );
        const runningSelfRequest = dispatch(
          marketplaceApi.util.getRunningQueryThunk("getMarketplaceIndividualById", individualByIdArgs),
        );
        trackRunningRequest(runningSelfRequest, selfRequest, context);
        const selfResultResponse = await selfRequest;
        if (!isLatestRequest(context)) return;

        if ("error" in selfResultResponse) {
          if (!isExpectedAbortError(selfResultResponse.error)) {
            throw selfResultResponse.error;
          }
        }

        const self = "data" in selfResultResponse ? selfResultResponse.data : undefined;
        if (self) {
          const selfResult: Hit<MarketplaceSearchInfoJSON> = {
            data: self as unknown as MarketplaceSearchInfoJSON,
            score: 0
          };
          hits = [selfResult, ...hits];
        }
      }

      if (!isLatestRequest(context)) return;
      setResults(hits);
      const responseSize = response.size || size;
      const responseFrom = response.from ?? from;
      const total = response.total ?? hits.length;
      if (
        response.total !== null &&
        response.total !== undefined &&
        resetOutOfRangePage(response.total, responseFrom, context)
      ) {
        return;
      }
      setNumResults(total);
      setPage(Math.floor(responseFrom / responseSize) + 1);
      setRowsPerPage(responseSize);

      onResults(total);
    } catch (error) {
      if (isLatestRequest(context) && !isExpectedAbortError(error)) {
        console.error(error);
        enqueueSnackbar("Couldn't get results. Please try again", {
          variant: "error",
        });
      }
    }
  }, [
    dispatch,
    isLatestRequest,
    onResults,
    resetOutOfRangePage,
    rowsPerPage,
    selectingTeam,
    selectingTeamLeader,
    selectingTeamMember,
    session,
    trackRunningRequest,
  ]);

  const searchParamsStr = searchParams?.toString() ?? "";
  const filtersJson = JSON.stringify(filters ?? {});
  const effectiveTermForSignature = searchParams?.get("query") ?? searchTerm ?? "";
  const effectiveFiltersForSignature: Dictionary<string> = { ...(filters ?? {}) };
  if (effectiveTermForSignature) effectiveFiltersForSignature.query = effectiveTermForSignature;
  if (category) effectiveFiltersForSignature.category = category;
  if (sortBy) effectiveFiltersForSignature.sortBy = sortBy;
  const requestFiltersSignature = JSON.stringify(effectiveFiltersForSignature);
  const requestSearchParamsSignature = JSON.stringify({
    id: searchParams?.get("id") ?? "",
    type: searchParams?.get("type") ?? "",
    pageIndex: getPageIndex(searchParams),
    suppressedInferences: getSuppressedInferences(searchParams).sort(),
  });

  useEffect(() => {
    const querySignature = [
      String(aiMode ?? false),
      requestSearchParamsSignature,
      requestFiltersSignature,
      String(retryNonce),
    ].join("|");

    if (hasInitializedQueryEffectRef.current && lastQuerySignatureRef.current === querySignature) {
      return;
    }

    hasInitializedQueryEffectRef.current = true;
    lastQuerySignatureRef.current = querySignature;
    const context = beginSearchRequest(querySignature);
    setQueryEffectPending(true);

    async function checkQueryParams() {
      const currentSearchParams = new URLSearchParams(searchParamsStr);
      const currentFilters = JSON.parse(filtersJson) as Dictionary<string>;
      const forceRefetch = Object.keys(currentFilters).length === 0;
      const id = currentSearchParams.get("id") ?? "";
      const type = currentSearchParams.get("type") ?? "";

      if (id !== "" && type !== "") {
        if (isLatestRequest(context)) navigate?.(`/marketplace/${type}/${id}`);
        return;
      }

      const term = currentSearchParams.get("query") ?? searchTerm ?? "";
      const queryChanged = lastQueryTermRef.current !== term;
      lastQueryTermRef.current = term;

      if (queryChanged || !term.trim()) {
        clearAssistedQueryState(context);
        clearSuppressedInferences(context);
        // `setSearchParams` updates the URL on the next render. Remove the query-owned values from
        // this request snapshot too, so query B never inherits a suppression that belonged to A.
        currentSearchParams.delete(SuppressedInferencesQueryParam);
      }

      const queryFilters: Dictionary<string> = {
        ...currentFilters,
      };

      if (term) {
        queryFilters.query = term;
      }

      if (category) {
        queryFilters.category = category;
      }

      if (sortBy) {
        queryFilters.sortBy = sortBy;
      }

      const pageIndex = getPageIndex(currentSearchParams);
      const from = pageIndex * rowsPerPage;
      if (isLatestRequest(context)) setPage(pageIndex + 1);
      const assistedAttemptKey = JSON.stringify({
        term,
        category,
        sortBy,
        queryFilters,
        from,
        size: rowsPerPage,
        retryNonce,
      });

      // AI mode owns the search only while it can answer it. A transport or schema failure falls
      // through to the ordinary endpoint with the same filters and page rather than emptying the
      // results or quietly returning page one.
      if (aiMode && canUseAssistedSearch && term.trim()) {
        if (failedAssistedAttemptRef.current !== assistedAttemptKey) {
          const ordinaryRequest = MarketplaceSearchRequestSchema.deserialize(queryFilters);
          const explicitFilters = toAssistedExplicitFilters(ordinaryRequest);
          const suppressedInferences = getSuppressedInferences(currentSearchParams);
          if (
            await fetchAssistedResults(
              term,
              explicitFilters,
              suppressedInferences,
              from,
              rowsPerPage,
              sortBy || undefined,
              context,
              assistedAttemptKey,
            )
          ) {
            return;
          }
        } else if (isLatestRequest(context)) {
          setAssistedLoading(false);
        }
      } else {
        failedAssistedAttemptRef.current = "";
        clearAssistedQueryState(context);
        if (isLatestRequest(context)) setAssistedLoading(false);
      }

      await fetchResultList(term, queryFilters, {
        context,
        forceRefetch,
        from,
        size: rowsPerPage,
      });
    }

    void checkQueryParams().finally(() => {
      if (isLatestRequest(context)) setQueryEffectPending(false);
    });
  }, [
    searchTerm,
    category,
    sortBy,
    aiMode,
    searchParamsStr,
    requestSearchParamsSignature,
    requestFiltersSignature,
    filtersJson,
    rowsPerPage,
    retryNonce,
    beginSearchRequest,
    clearAssistedQueryState,
    clearSuppressedInferences,
    fetchResultList,
    fetchAssistedResults,
    isLatestRequest,
    canUseAssistedSearch,
    isMarketplaceSearchMode,
    navigate,
  ]);

  async function handlePageChange(_event: ChangeEvent<unknown>, nextPage: number) {
    setResults([]);
    setPage(nextPage);

    if (searchParams && setSearchParams && isMarketplaceSearchMode) {
      invalidateActiveRequest();
      setAssistedLoading(false);
      const params = new URLSearchParams(searchParams); // clone, do not mutate prop object
      params.set("pageIndex", (nextPage - 1).toString());
      setSearchParams(params);
      return;
    }

    const from = (nextPage - 1) * rowsPerPage;
    const directSignature = JSON.stringify({
      searchTerm,
      filters,
      sortBy,
      from,
      size: rowsPerPage,
      aiMode,
      retryNonce,
    });
    const context = beginSearchRequest(directSignature);
    if (aiMode && canUseAssistedSearch && searchTerm.trim()) {
      const ordinaryRequest = MarketplaceSearchRequestSchema.deserialize(filters);
      if (
        await fetchAssistedResults(
          searchTerm,
          toAssistedExplicitFilters(ordinaryRequest),
          getSuppressedInferences(searchParams),
          from,
          rowsPerPage,
          sortBy || undefined,
          context,
          directSignature,
        )
      ) {
        return;
      }
    }

    await fetchResultList(searchTerm, filters, { context, from, size: rowsPerPage });
  }

  function searchExactWords() {
    if (!searchParams || !setSearchParams) return;
    invalidateActiveRequest();
    failedAssistedAttemptRef.current = "";
    setAssistedLoading(false);
    clearAssistedQueryState(undefined);
    const params = new URLSearchParams(searchParams);
    params.delete("aiMode");
    params.delete(SuppressedInferencesQueryParam);
    params.set("pageIndex", "0");
    setSearchParams(params);
  }

  function retryAssistedSearch() {
    invalidateActiveRequest();
    failedAssistedAttemptRef.current = "";
    setAssistedLoading(false);
    clearAssistedQueryState(undefined);
    setRetryNonce((current) => current + 1);
  }

  const assistedOutcomeMessage = getAssistedOutcomeMessage(assistedOutcome);

  return (
    <ComponentContainer>
      <Stack sx={{ flex: 1, width: "100%" }}>
        <Grid>
          {!assistedLoading && aiMode && assistedOutcome === "assisted" && numResults > 0 && (
            <AssistedModeIndicator role="status" aria-live="polite">
              <AutoAwesomeIcon color="primary" fontSize="small" />
              <Typography variant="caption">AI search</Typography>
              <Button size="small" onClick={searchExactWords}>Search exact words instead</Button>
            </AssistedModeIndicator>
          )}
          {assistedLoading && (
            // Says what is happening and why it is slow. A spinner alone would read as the app
            // being broken, since this takes seconds rather than milliseconds.
            <AssistedNotice role="status" aria-live="polite">
              <CircularProgress aria-label="AI search in progress" size={16} />
              <Typography variant="body2">Reading your search&hellip; this takes a few seconds.</Typography>
              <Button size="small" onClick={searchExactWords}>Search exact words instead</Button>
            </AssistedNotice>
          )}
          {!assistedLoading && aiMode && assistedOutcomeMessage && (
            <AssistedNotice role="status" aria-live="polite">
              <Typography variant="body2">{assistedOutcomeMessage}</Typography>
              {assistedOutcome === "parseTimedOut" && (
                <Button size="small" onClick={retryAssistedSearch}>Retry AI search</Button>
              )}
              <Button size="small" onClick={searchExactWords}>Search exact words instead</Button>
            </AssistedNotice>
          )}
          {!assistedLoading && aiMode && supersededInferences.length > 0 && (
            <AssistedNotice role="status" aria-live="polite">
              <Typography variant="body2">
                Your sidebar filters were kept instead of conflicting AI suggestions for{" "}
                {supersededInferences.map(getAssistedFilterLabel).join(", ")}.
              </Typography>
            </AssistedNotice>
          )}
          {!assistedLoading && aiMode && declinedConstraints.length > 0 && (
            // The marketplace has no field for these, so the search did not narrow by them. Naming
            // them is the difference between a result set the user can reason about and one that
            // silently ignored half their sentence.
            <AssistedNotice role="status" aria-live="polite">
              <Typography variant="body2">
                Not used in this search, because the marketplace has no field for{" "}
                {declinedConstraints.length === 1 ? "it" : "them"}: {declinedConstraints.join(", ")}.
              </Typography>
            </AssistedNotice>
          )}
          {!loading && aiMode && assistedOutcome === "assisted" && numResults === 0 && (
            <AssistedNotice role="status" aria-live="polite">
              <Typography variant="body2">No profiles matched the filters read from your request.</Typography>
              <Button size="small" onClick={searchExactWords}>Search exact words instead</Button>
            </AssistedNotice>
          )}
          {loading && <ResultSkeleton />}
          {!loading && (
            <UpperPagination container>
              <Grid>
                <ResultCount aria-live="polite" variant="h6">
                  {numResults} Result
                  {numResults < 1 || numResults > 1 ? "s" : ""}
                </ResultCount>
              </Grid>
              {numResults > rowsPerPage && (
                <Grid>
                  <Pagination
                    count={Math.ceil(numResults / rowsPerPage)}
                    page={page}
                    size={isMediumDisplaySize ? "medium" : "large"}
                    onChange={handlePageChange}
                  />
                </Grid>
              )}
            </UpperPagination>
          )}
        </Grid>
        {!loading && (
          <ResultsList
            results={results}
            embedded={embedded}
            selectingTeamLeader={selectingTeamLeader}
            selectingTeamMember={selectingTeamMember}
            selectingTeam={selectingTeam}
            selectedTeamMemberUserIds={selectedTeamMemberUserIds}
            compactLayout={compactLayout}
            navigate={navigate}
            searchParams={searchParams}
            onTeamMemberSelected={onTeamMemberSelected}
            onTeamSelected={onTeamSelected}
          />
        )}
        {numResults > rowsPerPage && !loading && (
          <LowerPagination size="grow">
            <Pagination
              count={Math.ceil(numResults / rowsPerPage)}
              page={page}
              disabled={Math.ceil(numResults / rowsPerPage) === 1}
              size={isMediumDisplaySize ? "medium" : "large"}
              onChange={handlePageChange}
            />
          </LowerPagination>
        )}
      </Stack>
    </ComponentContainer>
  );
}

//#endregion
